import React, { FC, useState, useEffect, useRef } from "react";
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  notification,
  Modal,
  Select,
  Typography,
  Divider,
  Statistic,
  Row,
  Col,
  Form,
  InputNumber,
  Input,
  Pagination
} from "antd";
import {
  ReloadOutlined,
  PlayCircleOutlined,
  QuestionCircleOutlined,
  CheckCircleOutlined,
  InfoCircleOutlined,
  EditOutlined,
  ArrowRightOutlined,
  SearchOutlined,
  FileTextOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import AppHeader from '../components/AppHeader';
import PageLayout from '../components/PageLayout';
import { fullname, role as roleKey, email as emailKey } from '../utils/data';
import { useGetScoringQuestions } from '../utils/hooks';
import { populateScoringQuestions, getAuthToken, updateScoringQuestion, getCategoryWeightages, updateCategoryWeightage, getLikelihoodScores, updateLikelihoodScore } from '../utils/functions';
import { CaseScoringQuestionsUrl, CategoryWeightagesUrl, LikelihoodScoresUrl } from '../utils/network';
import axios from 'axios';
import ArrowRight from "../components/svg/ArrowRight";
import ArrowRightWhite from "../components/svg/ArrowRightWhite";

const { Title, Text } = Typography;
const { Option } = Select;

interface ScoringQuestion {
  id: number;
  category: string;
  category_display: string;
  basis: string | null;
  case_type: string | null;
  question: string;
  answer: string;
  score: number;
  description: string | null;
  order: number;
}

const ScoringMethod: FC = () => {
  const navigate = useNavigate();
  const [questions, setQuestions] = useState<ScoringQuestion[]>([]);
  const [loading, setLoading] = useState(false);
  const [populating, setPopulating] = useState(false);
  const [filters, setFilters] = useState<{ category?: string; case_type?: string; basis?: string }>({});
  const [populateModalVisible, setPopulateModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState<ScoringQuestion | null>(null);
  const [form] = Form.useForm();
  const [updating, setUpdating] = useState(false);
  const [pageSize, setPageSize] = useState(20);
  const [algorithmModalVisible, setAlgorithmModalVisible] = useState(false);
  const [weightages, setWeightages] = useState<any[]>([]);
  const [likelihoodScores, setLikelihoodScores] = useState<any[]>([]);
  const [loadingWeightages, setLoadingWeightages] = useState(false);
  const [loadingLikelihood, setLoadingLikelihood] = useState(false);
  const [updatingWeightage, setUpdatingWeightage] = useState<number | null>(null);
  const [updatingLikelihood, setUpdatingLikelihood] = useState<number | null>(null);
  const [weightageForm] = Form.useForm();
  const [likelihoodForm] = Form.useForm();
  const [currentPage, setCurrentPage] = useState(1);
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const [scrollInfo, setScrollInfo] = useState({ left: 0, width: 0, client: 0 });

  // Get current user info from localStorage
  const currentUser = {
    role: localStorage.getItem(roleKey) || 'user',
    fullname: localStorage.getItem(fullname) || 'User',
    email: localStorage.getItem(emailKey) || ''
  };

  // Restrict access to admin users only
  useEffect(() => {
    if (!currentUser.role || currentUser.role.toLowerCase() !== 'admin') {
      navigate('/');
    }
  }, [currentUser.role, navigate]);

  // Use hook to fetch scoring questions
  useGetScoringQuestions(setQuestions, setLoading, filters);

  const handlePopulate = async (overwrite: boolean = false) => {
    setPopulating(true);
    try {
      const result = await populateScoringQuestions(overwrite);
      notification.success({
        message: 'Success',
        description: `Scoring questions populated successfully! Created: ${result.created}, Skipped: ${result.skipped}`,
        duration: 5,
      });
      setPopulateModalVisible(false);
      // Refresh the list
      setLoading(true);
      const headers = getAuthToken();
      if (headers) {
        const response = await axios.get(CaseScoringQuestionsUrl, headers);
        setQuestions(response.data);
      }
      setLoading(false);
    } catch (error: any) {
      notification.error({
        message: 'Error',
        description: error.response?.data?.message || 'Failed to populate scoring questions. Please try again.',
      });
    } finally {
      setPopulating(false);
    }
  };

  const pagedData = React.useMemo(() => questions.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  ), [questions, currentPage, pageSize]);

  // Custom Scrollbar Sync Logic
  useEffect(() => {
    const tableBody = tableContainerRef.current?.querySelector('.ant-table-content');
    if (!tableBody) return;

    const onScroll = () => {
      setScrollInfo({
        left: tableBody.scrollLeft,
        width: tableBody.scrollWidth,
        client: tableBody.clientWidth
      });
    };

    tableBody.addEventListener('scroll', onScroll);
    onScroll(); // Initial sync

    window.addEventListener('resize', onScroll);
    
    return () => {
      tableBody.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [questions, loading, pagedData]); 

  const handleFilterChange = (key: string, value: string | undefined) => {
    setFilters(prev => ({
      ...prev,
      [key]: value || undefined
    }));
  };

  const clearFilters = () => {
    setFilters({});
  };

  const handleEdit = (record: ScoringQuestion) => {
    setEditingQuestion(record);
    form.setFieldsValue({
      score: record.score,
      description: record.description || '',
      answer: record.answer,
      order: record.order
    });
    setEditModalVisible(true);
  };

  const handleUpdate = async () => {
    if (!editingQuestion) return;

    try {
      const values = await form.validateFields();
      setUpdating(true);

      await updateScoringQuestion(editingQuestion.id, {
        score: values.score,
        description: values.description,
        answer: values.answer,
        order: values.order
      });

      notification.success({
        message: 'Success',
        description: 'Scoring question updated successfully!',
      });

      setEditModalVisible(false);
      setEditingQuestion(null);
      form.resetFields();

      // Refresh the list
      setLoading(true);
      const headers = getAuthToken();
      if (headers) {
        const response = await axios.get(CaseScoringQuestionsUrl, headers);
        setQuestions(response.data);
      }
      setLoading(false);
    } catch (error: any) {
      if (error.errorFields) {
        // Form validation error
        return;
      }
      notification.error({
        message: 'Error',
        description: error.response?.data?.message || 'Failed to update scoring question. Please try again.',
      });
    } finally {
      setUpdating(false);
    }
  };

  const columns = [
    {
      title: <span className="font-['Lexend',sans-serif] font-semibold text-[#475569]">Question</span>,
      dataIndex: 'question',
      key: 'question',
      width: '30%',
      render: (text: string) => <Text strong>{text}</Text>
    },
    {
      title: <span className="font-['Lexend',sans-serif] font-semibold text-[#475569]">Answer</span>,
      dataIndex: 'answer',
      key: 'answer',
      width: '25%',
    },
    {
      title: <span className="font-['Lexend',sans-serif] font-semibold text-[#475569]">Score</span>,
      dataIndex: 'score',
      key: 'score',
      width: '10%',
      render: (score: number) => (
        <Tag
          className="rounded-full px-3 py-0.5 border-none font-medium"
          style={{
            backgroundColor: score >= 8 ? '#fee2e2' : score >= 5 ? '#ffedd5' : '#dcfce7',
            color: score >= 8 ? '#991b1b' : score >= 5 ? '#9a3412' : '#166534'
          }}
        >
          {score}
        </Tag>
      ),
      sorter: (a: ScoringQuestion, b: ScoringQuestion) => a.score - b.score,
    },
    {
      title: <span className="font-['Lexend',sans-serif] font-semibold text-[#475569]">Category</span>,
      dataIndex: 'category_display',
      key: 'category',
      width: '15%',
      render: (text: string) => (
        <Tag
          className="rounded-full px-3 py-0.5 border-none font-medium"
          style={{ backgroundColor: '#f1f5f9', color: '#475569' }}
        >
          {text}
        </Tag>
      )
    },
    {
      title: <span className="font-['Lexend',sans-serif] font-semibold text-[#475569]">Case Type</span>,
      dataIndex: 'case_type',
      key: 'case_type',
      width: '15%',
      render: (text: string | null) => text ? (
        <Tag
          className="rounded-full px-3 py-0.5 border-none font-medium"
          style={{ backgroundColor: '#ecfdf5', color: '#065f46' }}
        >
          {text}
        </Tag>
      ) : (
        <Tag className="rounded-full px-3 py-0.5 bg-gray-100/80 text-gray-400 border-none">All</Tag>
      )
    },
    {
      title: <span className="font-['Lexend',sans-serif] font-semibold text-[#475569]">Basis</span>,
      dataIndex: 'basis',
      key: 'basis',
      width: '12%',
      render: (text: string | null) => text ? (
        <Tag
          className="rounded-full px-3 py-0.5 border-none font-medium"
          style={{ backgroundColor: '#f3e8ff', color: '#6b21a8' }}
        >
          {text}
        </Tag>
      ) : (
        <Tag className="rounded-full px-3 py-0.5 bg-gray-100/80 text-gray-400 border-none">All</Tag>
      )
    },
    {
      title: <span className="font-['Lexend',sans-serif] font-semibold text-[#475569]">Actions</span>,
      key: 'actions',
      width: '8%',
      render: (_: any, record: ScoringQuestion) => (
        <Button
          type="primary"
          className="bg-[#4682B4] rounded-lg flex items-center gap-2 h-8 px-4 text-[12px] font-semibold border-none"
          onClick={() => handleEdit(record)}
        >
          <EditOutlined className="text-sm" /> Edit
        </Button>
      ),
    },
  ];

  const totalQuestions = new Set(questions.map(q => q.question)).size;
  const totalAnswers = questions.length;
  const avgScore = questions.length > 0
    ? (questions.reduce((sum, q) => sum + q.score, 0) / questions.length).toFixed(2)
    : 0;

  return (
    <PageLayout>
      <AppHeader currentUser={currentUser} />
      <div className="relative z-10 w-full max-w-[1440px] mx-auto px-6 md:px-12 mt-12 md:mt-12 pb-12">
        {/* Combined Header and Filters Card */}
        <Card
          className="premium-gradient-card white mb-6 overflow-hidden rounded-[17px] bg-white"
          bodyStyle={{ padding: '24px 41px' }}
        >
          <div className="flex flex-col gap-8">
            {/* Header Row */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
              <div className="flex items-center gap-5">
                <div className="bg-[#4682B4]/10 p-3.5 rounded-[14px]">
                  <FileTextOutlined className="text-[#4682B4] text-[26px]" />
                </div>
                <div>
                  <h1 className="!m-0 font-['Lexend',sans-serif] font-semibold text-[#1f2a37] text-[14px] md:text-[20px] leading-tight">
                    Scoring Method
                  </h1>
                  <p className="text-[#64748b] font-['DM_Sans',sans-serif] text-[12px] md:text-[14px] mt-1 font-normal">
                    Manage case scoring questions and answers with default scores
                  </p>
                </div>
              </div>
              <Space wrap size={16}>
                <Button
                  icon={<ReloadOutlined />}
                  className="flex items-center gap-2 rounded-lg h-10 px-5 font-medium border-[#e5e7eb] hover:!text-[#4682B4] hover:!border-[#4682B4]"
                  onClick={async () => {
                    setLoading(true);
                    const headers = getAuthToken();
                    if (headers) {
                      try {
                        const response = await axios.get(CaseScoringQuestionsUrl, headers);
                        setQuestions(response.data);
                      } catch (error) {
                        console.error('Error refreshing:', error);
                      }
                    }
                    setLoading(false);
                  }}
                  loading={loading}
                >
                  Refresh
                </Button>
                <Button
                  type="primary"
                  icon={<PlayCircleOutlined />}
                  className="bg-[#4682B4] border-none flex items-center gap-2 rounded-lg h-10 px-6 font-medium hover:!bg-[#4682B4]/90"
                  onClick={() => setPopulateModalVisible(true)}
                >
                  Populate Defaults
                </Button>
                <Button
                  type="default"
                  icon={<InfoCircleOutlined />}
                  className="flex items-center gap-2 rounded-lg h-10 px-5 font-medium border-[#e5e7eb] hover:!text-[#4682B4] hover:!border-[#4682B4]"
                  onClick={() => {
                    setAlgorithmModalVisible(true);
                    getCategoryWeightages(setWeightages, setLoadingWeightages);
                    getLikelihoodScores(setLikelihoodScores, setLoadingLikelihood);
                  }}
                >
                  Algorithm Settings
                </Button>
              </Space>
            </div>

            {/* Filters Row */}
            <div className="flex flex-wrap items-end gap-x-8 gap-y-6">
              <div className="flex flex-col gap-2 min-w-[200px] flex-1">
                <label className="font-['Lexend',sans-serif] font-semibold text-[#1f2a37] text-[14px]">Category</label>
                <Select
                  placeholder="Select Category"
                  allowClear
                  className="h-11 w-full [&>.ant-select-selector]:!rounded-lg [&>.ant-select-selector]:!border-[#e5e7eb] hover:[&>.ant-select-selector]:!border-[#4682B4]"
                  value={filters.category}
                  onChange={(value) => handleFilterChange('category', value)}
                >
                  <Option value="B">Behavior</Option>
                  <Option value="S">Situation</Option>
                  <Option value="D">Duration</Option>
                  <Option value="R">Result/Impact</Option>
                  <Option value="W">Weight/Severity</Option>
                  <Option value="F">Frequency</Option>
                </Select>
              </div>

              <div className="flex flex-col gap-2 min-w-[200px] flex-1">
                <label className="font-['Lexend',sans-serif] font-semibold text-[#1f2a37] text-[14px]">Case Type</label>
                <Select
                  placeholder="Select Type"
                  allowClear
                  className="h-11 w-full [&>.ant-select-selector]:!rounded-lg [&>.ant-select-selector]:!border-[#e5e7eb] hover:[&>.ant-select-selector]:!border-[#4682B4]"
                  value={filters.case_type}
                  onChange={(value) => handleFilterChange('case_type', value)}
                >
                  <Option value="Accommodation">Accommodation</Option>
                  <Option value="Different Treatment">Different Treatment</Option>
                  <Option value="Harassment">Harassment</Option>
                  <Option value="Nonselection">Nonselection</Option>
                  <Option value="Sexual Harassment">Sexual Harassment</Option>
                  <Option value="Termination">Termination</Option>
                </Select>
              </div>

              <div className="flex flex-col gap-2 min-w-[200px] flex-1">
                <label className="font-['Lexend',sans-serif] font-semibold text-[#1f2a37] text-[14px]">Basis</label>
                <Select
                  placeholder="Select Basis"
                  allowClear
                  className="h-11 w-full [&>.ant-select-selector]:!rounded-lg [&>.ant-select-selector]:!border-[#e5e7eb] hover:[&>.ant-select-selector]:!border-[#4682B4]"
                  value={filters.basis}
                  onChange={(value) => handleFilterChange('basis', value)}
                >
                  <Option value="Age">Age</Option>
                  <Option value="Color">Color</Option>
                  <Option value="Mental/Physical Handicap">Handicap</Option>
                  <Option value="National Origin">Origin</Option>
                  <Option value="Race">Race</Option>
                  <Option value="Religion">Religion</Option>
                  <Option value="Reprisal">Reprisal</Option>
                  <Option value="Sex">Sex</Option>
                </Select>
              </div>

              <div className="flex justify-between md:justify-start gap-2 md:gap-[24px] mt-[29px] md:mt-0">
                <Button 
                  className="pr-[14px] pl-[6px] py-[5px] md:pr-[25px] md:pl-[9px] md:py-[10px] rounded-full bg-[#4682B4] hover:bg-[#3b6d96] text-white font-['Lexend',sans-serif] font-normal md:font-medium text-[12px] md:text-[16px] flex items-center gap-[10px] md:gap-[13px] border-none cursor-pointer transition-colors h-[32px] md:h-[47px]"
                  onClick={() => { }} // Hook handles change automatically
                >
                   <div className="w-[22px] h-[22px] md:w-[29px] md:h-[29px] rounded-full bg-white flex items-center justify-center text-[#4682B4]">
                    <div className="scale-[0.6] md:scale-100 flex items-center justify-center"><ArrowRight /></div>
                    </div>
                  Search
                </Button>
                <Button 
                  className="pr-[14px] pl-[6px] py-[5px] md:pr-[25px] md:pl-[9px] md:py-[10px] rounded-full bg-white border border-[#4682B4] text-[#1F2A37] font-['Lexend',sans-serif] font-normal md:font-medium text-[12px] md:text-[16px] flex items-center gap-[10px] md:gap-[13px] cursor-pointer transition-colors hover:bg-[#f8FAFC] h-[32px] md:h-[47px]"
                  onClick={clearFilters}
                >
                  <div className="w-[22px] h-[22px] md:w-[29px] md:h-[29px] flex items-center justify-center">
                    <div className="scale-[0.73] md:scale-100 flex items-center justify-center origin-center"><ArrowRightWhite /></div>
                  </div>
                  Clear
                </Button>
              </div>
            </div>
          </div>
        </Card>

        {/* Statistics Tiles */}
        <Row gutter={[24, 24]} className="mb-8">
          <Col xs={24} sm={8}>
            <Card className="premium-card white hover:shadow-md transition-all duration-300">
              <Statistic
                title={<span className="font-['Lexend',sans-serif] font-semibold text-[#475569] text-[15px]">Total Questions</span>}
                value={totalQuestions}
                prefix={<QuestionCircleOutlined className="text-[#4682B4] mr-2" />}
                valueStyle={{ color: '#1f2a37', fontWeight: 700, fontFamily: 'DM Sans, sans-serif', fontSize: '28px' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card className="premium-card white hover:shadow-md transition-all duration-300">
              <Statistic
                title={<span className="font-['Lexend',sans-serif] font-semibold text-[#475569] text-[15px]">Total Options</span>}
                value={totalAnswers}
                prefix={<CheckCircleOutlined className="text-[#4682B4] mr-2" />}
                valueStyle={{ color: '#1f2a37', fontWeight: 700, fontFamily: 'DM Sans, sans-serif', fontSize: '28px' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card className="premium-card white hover:shadow-md transition-all duration-300">
              <Statistic
                title={<span className="font-['Lexend',sans-serif] font-semibold text-[#475569] text-[15px]">Avg Score</span>}
                value={avgScore}
                prefix={<InfoCircleOutlined className="text-[#4682B4] mr-2" />}
                valueStyle={{ color: '#1f2a37', fontWeight: 700, fontFamily: 'DM Sans, sans-serif', fontSize: '28px' }}
              />
            </Card>
          </Col>
        </Row>

        {/* Questions Table Card */}
        <style dangerouslySetInnerHTML={{__html: `
          .modern-table .ant-table-thead > tr > th {
            background: #4682B417 !important;
            color: #1F2A37 !important;
            font-weight: 400 !important;
            font-family: 'Lexend', sans-serif !important;
            border-bottom: none !important;
            padding: 16px !important;
            font-size: 10px !important;
          }
          @media (min-width: 768px) {
            .modern-table .ant-table-thead > tr > th {
              font-size: 14px !important;
            }
          }
          .modern-table .ant-table {
            background: transparent !important;
          }
          .modern-table .ant-table-container {
            background: transparent !important;
          }
          .modern-table .ant-table-row {
            transition: all 0.2s;
            background: transparent !important;
          }
          .modern-table .ant-table-row:hover {
            background-color: #F8FAFC !important;
          }
          .modern-table .ant-table-cell {
            border-bottom: 1px solid #F1F5F9 !important;
            padding: 16px !important;
            font-family: 'Lexend', sans-serif !important;
            background: transparent !important;
            font-size: 14px !important;
          }
          .modern-table .ant-table-content::-webkit-scrollbar {
            display: none !important;
          }
          .modern-table .ant-table-content {
            -ms-overflow-style: none !important;
            scrollbar-width: none !important;
          }
        `}} />
        <Card
          className="premium-gradient-card white overflow-hidden rounded-[17px] shadow-sm bg-white"
          bodyStyle={{ padding: '16px' }}
        >
          <div ref={tableContainerRef}>
            <Table
              columns={columns}
              dataSource={pagedData}
              rowKey="id"
              loading={loading}
              className="modern-table"
              scroll={{ x: 1000 }}
              pagination={false}
              expandable={{
                expandedRowRender: (record: ScoringQuestion) => (
                  <div className="p-4 bg-[#f8fafc] rounded-lg mx-4 mb-4 border border-blue-50">
                    <Text strong className="text-[#475569]">Description: </Text>
                    <Text className="text-[#64748b] ml-1">{record.description || 'No description'}</Text>
                  </div>
                ),
              }}
            />
          </div>
        </Card>

        {/* Custom Synchronized Scrollbar Outside the card */}
        {scrollInfo.width > scrollInfo.client && (
          <div className="w-full flex justify-center mt-[19px]">
            <div 
              className="relative h-[4px] rounded-[3px] overflow-hidden" 
              style={{ 
                width: '85.90%', 
                backgroundColor: '#4682B430' 
              }}
            >
              <div 
                className="absolute h-full bg-[#4682B4] rounded-[3px] transition-all duration-100"
                style={{
                  width: `${(scrollInfo.client / scrollInfo.width) * 60}%`,
                  left: `${(scrollInfo.left / (scrollInfo.width - scrollInfo.client || 1)) * (100 - (scrollInfo.client / scrollInfo.width * 60))}%`
                }}
              />
            </div>
          </div>
        )}

        {/* Separate Pagination outside the card */}
        <div className="w-full flex justify-end mt-[22px] md:mt-[42px]">
          <style dangerouslySetInnerHTML={{__html: `
            .custom-pagination-container {
              display: flex !important;
              align-items: center !important;
              justify-content: flex-end !important;
              width: 100% !important;
            }
            .custom-pagination-container .ant-pagination-total-text {
              order: 1 !important;
              color: #555D67 !important;
              font-family: 'Lexend', sans-serif !important;
              font-size: 10px !important;
              font-weight: 300 !important;
              margin-right: 8px !important;
            }
            @media (min-width: 768px) {
              .custom-pagination-container .ant-pagination-total-text {
                font-size: 14px !important;
                margin-right: 12px !important;
              }
            }
            /* Arrows Styling */
            .custom-pagination-container .ant-pagination-prev,
            .custom-pagination-container .ant-pagination-next {
              order: 2 !important;
              border: none !important;
              background: transparent !important;
              min-width: 20px !important;
              height: 32px !important;
              display: flex !important;
              align-items: center !important;
              justify-content: center !important;
              color: #1F2A37 !important;
              font-size: 16px !important;
              margin: 0 !important;
            }
            /* Page Item & Jump (Dots) Styling */
            .custom-pagination-container .ant-pagination-item,
            .custom-pagination-container .ant-pagination-jump-prev,
            .custom-pagination-container .ant-pagination-jump-next {
              order: 2 !important;
              border-radius: 4px !important;
              height: 28px !important;
              min-width: 28px !important;
              line-height: 26px !important;
              background: white !important;
              font-family: 'Lexend', sans-serif !important;
              margin: 0 4px !important;
              font-size: 14px !important;
              display: inline-flex !important;
              align-items: center !important;
              justify-content: center !important;
            }
            .custom-pagination-container .ant-pagination-item {
              border: 0.8px solid #4682B4 !important;
            }
            .custom-pagination-container .ant-pagination-jump-prev,
            .custom-pagination-container .ant-pagination-jump-next {
              border: none !important;
            }
            /* Hide non-active items on small screens */
            @media (max-width: 767px) {
              .custom-pagination-container .ant-pagination-item:not(.ant-pagination-item-active),
              .custom-pagination-container .ant-pagination-jump-prev,
              .custom-pagination-container .ant-pagination-jump-next {
                display: none !important;
              }
            }
            .custom-pagination-container .ant-pagination-item-active {
              border-color: #4682B4 !important;
            }
            .custom-pagination-container .ant-pagination-item-active a {
              color: #1F2A37 !important;
              font-weight: 500 !important;
            }
            /* Options/Page Changer Styling */
            .custom-pagination-container .ant-pagination-options {
              order: 3 !important;
              margin-left: 8px !important;
              display: block !important; /* Ensure it is shown on mobile */
            }
            .custom-pagination-container .ant-select-selector {
              background-color: #032B440D !important;
              border: none !important;
              border-radius: 4px !important;
              height: 28px !important;
              font-family: 'Lexend', sans-serif !important;
              font-size: 12px !important;
              font-weight: 300 !important;
              color: #1F2A37 !important;
              padding: 0 10px !important;
              display: flex !important;
              align-items: center !important;
              justify-content: center !important;
            }
            .custom-pagination-container .ant-select-selection-item {
              line-height: 28px !important;
              color: #1F2A37 !important;
            }
            .custom-pagination-container .ant-select-arrow {
              color: #1F2A37 !important;
            }
          `}} />
          <Pagination
            className="custom-pagination-container"
            current={currentPage}
            pageSize={pageSize}
            total={questions.length}
            onChange={(page, size) => {
              setCurrentPage(page);
              setPageSize(size);
            }}
            showSizeChanger
            showTotal={(total) => `Total ${total} answer options`}
            itemRender={(page, type, originalElement) => {
              if (type === 'prev') return <span className="cursor-pointer font-bold px-1">{"<"}</span>;
              if (type === 'next') return <span className="cursor-pointer font-bold px-1">{">"}</span>;
              return originalElement;
            }}
          />
        </div>
      </div>
      

      {/* Populate Modal */}
      <Modal
        title={<span className="font-['Lexend',sans-serif] font-semibold text-[#1f2a37] text-[16px] md:text-[18px]">Populate Default Scoring Questions</span>}
        open={populateModalVisible}
        onCancel={() => setPopulateModalVisible(false)}
        footer={null}
        width={560}
        style={{ top: 20 }}
        className="premium-modal responsive-modal"
        bodyStyle={{ padding: window.innerWidth < 768 ? '20px 16px' : '24px 32px' }}
      >
        <div className="font-['DM_Sans',sans-serif]">
          <p className="text-[#475569] text-[15px] leading-relaxed mb-6">
            This will populate the database with default scoring questions and answers based on your form fields.
          </p>
          
          <Divider className="my-6 border-[#f1f5f9]" />
          
          <div className="mb-6">
            <h4 className="font-['Lexend',sans-serif] font-semibold text-[#1f2a37] text-[14px] md:text-[15px] mb-3">Options:</h4>
            <div className="space-y-4 md:space-y-3">
              <div className="flex flex-col md:flex-row md:gap-2">
                <span className="font-semibold text-[#475569] text-[13px] md:text-[14px] shrink-0">Populate (Skip Existing):</span>
                <span className="text-[#64748b] text-[13px] md:text-[14px]">Creates new records but skips any that already exist</span>
              </div>
              <div className="flex flex-col md:flex-row md:gap-2">
                <span className="font-semibold text-[#475569] text-[13px] md:text-[14px] shrink-0">Overwrite All:</span>
                <span className="text-[#64748b] text-[13px] md:text-[14px]">Deletes all existing records and creates fresh ones</span>
              </div>
            </div>
          </div>
          
          <Divider className="my-6 border-[#f1f5f9]" />
          
          <p className="text-[#94a3b8] text-[13px]  leading-relaxed mb-8">
            This will create scoring questions for all case types and basis with predefined scores based on the scoring method reference table.
          </p>

          <div className="flex flex-col md:flex-row justify-end gap-3">
            <Button 
              onClick={() => setPopulateModalVisible(false)}
              className="h-10 md:h-11 px-6 rounded-lg font-semibold border-[#e5e7eb] text-[#64748b] hover:!text-[#1f2a37] hover:!border-[#cbd5e1] transition-all order-3 md:order-1"
            >
              Cancel
            </Button>
            <Button 
              danger
              ghost
              onClick={() => handlePopulate(true)} 
              loading={populating}
              className="h-10 md:h-11 px-6 rounded-lg font-semibold border-[#ff4d4f] text-[#ff4d4f] hover:!bg-[#fff1f0] transition-all order-2"
            >
              Overwrite All
            </Button>
            <Button 
              type="primary" 
              onClick={() => handlePopulate(false)} 
              loading={populating}
              className="h-10 md:h-11 px-8 rounded-lg font-semibold bg-[#4682B4] border-none shadow-lg shadow-[#4682B4]/20 hover:!bg-[#4682B4]/90 transition-all order-1 md:order-3"
            >
              Populate (Skip Existing)
            </Button>
          </div>
        </div>
      </Modal>

      {/* Edit Modal */}
      <Modal
        title={<span className="font-['Lexend',sans-serif] font-semibold text-[#1f2a37] text-[18px]">Edit Scoring Question</span>}
        open={editModalVisible}
        onCancel={() => {
          setEditModalVisible(false);
          setEditingQuestion(null);
          form.resetFields();
        }}
        footer={null}
        style={{ top: 20 }}
        width={540}
        className="premium-modal responsive-modal"
        bodyStyle={{ padding: window.innerWidth < 768 ? '20px 16px' : '24px 32px' }}
      >
        
        {editingQuestion && (
          <Form
            form={form}
            layout="vertical"
            initialValues={{
              score: editingQuestion.score,
              description: editingQuestion.description || '',
              answer: editingQuestion.answer,
              order: editingQuestion.order
            }}
            onFinish={handleUpdate}
          >
            <div className="mb-6">
              <label className="font-['Lexend',sans-serif] font-semibold text-[#1f2a37] text-[14px] mb-2 block">Question</label>
              <div className="text-[#94a3b8] font-['DM_Sans',sans-serif] text-[14px] leading-relaxed">
                {editingQuestion.question}
              </div>
            </div>

            <div className="mb-6">
              <label className="font-['Lexend',sans-serif] font-semibold text-[#1f2a37] text-[14px] mb-3 block">Category</label>
              <div className="inline-block px-4 py-1.5 rounded-lg border border-[#e5e7eb] bg-white font-['DM_Sans',sans-serif] text-[#475569] text-[14px]">
                {editingQuestion.category_display}
              </div>
            </div>

            <Divider className="my-6 border-[#f1f5f9]" />

            <Form.Item
              label={<span className="font-['Lexend',sans-serif] font-semibold text-[#1f2a37] text-[14px]">Answer</span>}
              name="answer"
              rules={[{ required: true, message: 'Please enter the answer' }]}
              className="mb-6"
            >
              <Input 
                placeholder="Answer text" 
                className="h-11 rounded-lg border-[#e5e7eb] hover:border-[#4682B4] focus:border-[#4682B4] font-['DM_Sans',sans-serif]" 
              />
            </Form.Item>

            <Form.Item
              label={<span className="font-['Lexend',sans-serif] font-semibold text-[#1f2a37] text-[14px]">Score</span>}
              name="score"
              rules={[
                { required: true, message: 'Please enter the score' },
                { type: 'number', min: 0, max: 10, message: 'Score must be between 0 and 10' }
              ]}
              className="mb-6"
            >
              <InputNumber
                min={0}
                max={10}
                style={{ width: '100%' }}
                placeholder="Score (0-10)"
                className="h-11 rounded-lg border-[#e5e7eb] hover:border-[#4682B4] focus:border-[#4682B4] font-['DM_Sans',sans-serif] flex items-center"
              />
            </Form.Item>

            <Form.Item
              label={<span className="font-['Lexend',sans-serif] font-semibold text-[#1f2a37] text-[14px]">Description</span>}
              name="description"
              className="mb-6"
            >
              <Input.TextArea
                rows={3}
                placeholder="Brief description of this answer score"
                className="rounded-lg border-[#e5e7eb] hover:border-[#4682B4] focus:border-[#4682B4] font-['DM_Sans',sans-serif] py-2.5"
              />
            </Form.Item>

            <Form.Item
              label={<span className="font-['Lexend',sans-serif] font-semibold text-[#1f2a37] text-[14px]">Order</span>}
              name="order"
              rules={[
                { required: true, message: 'Please enter the order' },
                { type: 'number', min: 1, message: 'Order must be at least 1' }
              ]}
              className="mb-8"
            >
              <InputNumber
                min={1}
                style={{ width: '100%' }}
                placeholder="Display order"
                className="h-11 rounded-lg border-[#e5e7eb] hover:border-[#4682B4] focus:border-[#4682B4] font-['DM_Sans',sans-serif] flex items-center"
              />
            </Form.Item>

            <div className="flex justify-end gap-3 mt-8">
              <Button 
                onClick={() => setEditModalVisible(false)}
                className="h-11 px-8 rounded-lg font-semibold border-[#e5e7eb] text-[#64748b] hover:!text-[#1f2a37] hover:!border-[#cbd5e1] transition-all"
              >
                Cancel
              </Button>
              <Button 
                type="primary"
                htmlType="submit"
                loading={updating}
                className="h-11 px-10 rounded-lg font-semibold bg-[#4682B4] border-none shadow-lg shadow-[#4682B4]/20 hover:!bg-[#4682B4]/90 transition-all"
              >
                Update
              </Button>
            </div>
          </Form>
        )}
      </Modal>

      {/* Algorithm Settings Modal */}
      <Modal
        title={<span className="font-['Lexend',sans-serif] font-semibold text-[#1f2a37] text-[18px]">Algorithm Settings</span>}
        open={algorithmModalVisible}
        onCancel={() => {
          setAlgorithmModalVisible(false);
          weightageForm.resetFields();
          likelihoodForm.resetFields();
        }}
        footer={null}
        width={750}
        style={{ top: 20 }}
        bodyStyle={{ 
          padding: window.innerWidth < 768 ? '16px 12px' : '24px 32px',
          maxHeight: '85vh',
          overflowY: 'auto'
        }}
        className="premium-modal responsive-modal"
      >
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-['Lexend',sans-serif] font-semibold text-[#1f2a37] text-[15px] m-0">Category Weightages</h3>
            <Button
              size="small"
              icon={<PlayCircleOutlined className="text-[12px]" />}
              className="rounded-lg h-8 flex items-center gap-2 border-[#e5e7eb] font-medium text-[#475569] hover:!text-[#4682B4] hover:!border-[#4682B4] transition-all"
              onClick={async () => {
                try {
                  const headers = getAuthToken();
                  if (headers) {
                    const response = await axios.post(
                      `${CategoryWeightagesUrl}populate_default/`,
                      { overwrite: false },
                      headers
                    );
                    notification.success({
                      message: 'Success',
                      description: response.data.message,
                    });
                    getCategoryWeightages(setWeightages, setLoadingWeightages);
                  }
                } catch (error: any) {
                  notification.error({
                    message: 'Error',
                    description: error.response?.data?.message || 'Failed to populate default weightages',
                  });
                }
              }}
            >
              Defaults
            </Button>
          </div>

          <Table
            dataSource={weightages}
            rowKey="id"
            loading={loadingWeightages}
            pagination={false}
            size="middle"
            className="modern-settings-table"
            columns={[
              {
                title: <span className="font-['Lexend',sans-serif] text-[13px] text-[#475569]">Category</span>,
                dataIndex: 'category_display',
                key: 'category',
                width: '30%',
                render: (text) => <span className="font-['DM_Sans',sans-serif] text-[#1f2a37] text-[14px]">{text}</span>
              },
              {
                title: <span className="font-['Lexend',sans-serif] text-[13px] text-[#475569]">Weightage</span>,
                key: 'weightage',
                width: '50%',
                render: (_: any, record: any) => (
                  <InputNumber
                    min={0}
                    max={1}
                    step={0.0001}
                    precision={4}
                    value={parseFloat(record.weightage)}
                    onChange={async (value) => {
                      if (value !== null && value !== undefined) {
                        setUpdatingWeightage(record.id);
                        try {
                          await updateCategoryWeightage(record.id, value);
                          getCategoryWeightages(setWeightages, setLoadingWeightages);
                        } catch (error) {
                          notification.error({
                            message: 'Error',
                            description: 'Failed to update weightage',
                          });
                        } finally {
                          setUpdatingWeightage(null);
                        }
                      }
                    }}
                    disabled={updatingWeightage === record.id}
                    className="w-full h-10 rounded-lg border-[#e5e7eb] hover:border-[#4682B4] focus:border-[#4682B4] flex items-center font-['DM_Sans',sans-serif]"
                  />
                ),
              },
              {
                title: <span className="font-['Lexend',sans-serif] text-[13px] text-[#475569]">Status</span>,
                key: 'status',
                width: '20%',
                align: 'center',
                render: (_: any, record: any) => (
                  updatingWeightage === record.id ? (
                    <Tag color="processing" className="m-0 px-3 py-1 rounded-md border-none">Updating</Tag>
                  ) : (
                    <div className="bg-[#f0fdf4] border border-[#bbf7d0] rounded-md px-3 py-1 text-[#166534] text-[12px] font-medium inline-block">
                      Saved
                    </div>
                  )
                ),
              },
            ]}
          />
  
        <Divider className="my-8 border-[#f1f5f9]" />

        <div className="mb-2">
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-['Lexend',sans-serif] font-semibold text-[#1f2a37] text-[15px] m-0">Likelihood Score Ranges</h3>
            <Button
              size="small"
              icon={<PlayCircleOutlined className="text-[12px]" />}
              className="rounded-lg h-8 flex items-center gap-2 border-[#e5e7eb] font-medium text-[#475569] hover:!text-[#4682B4] hover:!border-[#4682B4] transition-all"
              onClick={async () => {
                try {
                  const headers = getAuthToken();
                  if (headers) {
                    const response = await axios.post(
                      `${LikelihoodScoresUrl}populate_default/`,
                      { overwrite: false },
                      headers
                    );
                    notification.success({
                      message: 'Success',
                      description: response.data.message,
                    });
                    getLikelihoodScores(setLikelihoodScores, setLoadingLikelihood);
                  }
                } catch (error: any) {
                  notification.error({
                    message: 'Error',
                    description: error.response?.data?.message || 'Failed to populate default likelihood scores',
                  });
                }
              }}
            >
              Defaults
            </Button>
          </div>

          <Table
            dataSource={likelihoodScores}
            rowKey="id"
            loading={loadingLikelihood}
            pagination={false}
            size="middle"
            className="modern-settings-table"
            columns={[
              {
                title: <span className="font-['Lexend',sans-serif] text-[13px] text-[#475569]">Class</span>,
                dataIndex: 'class_name',
                key: 'class_name',
                width: '15%',
                render: (text: string) => (
                  <Tag
                    className="m-0 px-4 py-1 rounded-md border-none font-semibold text-[12px]"
                    style={{ 
                      backgroundColor: text === 'Low' ? '#f0fdf4' : text === 'Medium' ? '#fff7ed' : '#fef2f2',
                      color: text === 'Low' ? '#166534' : text === 'Medium' ? '#9a3412' : '#991b1b',
                      border: `1px solid ${text === 'Low' ? '#bbf7d0' : text === 'Medium' ? '#fed7aa' : '#fecaca'}`
                    }}
                  >
                    {text}
                  </Tag>
                ),
              },
              {
                title: <span className="font-['Lexend',sans-serif] text-[13px] text-[#475569]">Min Range</span>,
                key: 'min_range',
                width: '30%',
                render: (_: any, record: any) => (
                  <InputNumber
                    min={0}
                    step={0.01}
                    precision={2}
                    value={parseFloat(record.min_range)}
                    onChange={async (value) => {
                      if (value !== null && value !== undefined) {
                        setUpdatingLikelihood(record.id);
                        try {
                          await updateLikelihoodScore(record.id, { min_range: value });
                          getLikelihoodScores(setLikelihoodScores, setLoadingLikelihood);
                        } catch (error) {
                          notification.error({
                            message: 'Error',
                            description: 'Failed to update min range',
                          });
                        } finally {
                          setUpdatingLikelihood(null);
                        }
                      }
                    }}
                    disabled={updatingLikelihood === record.id}
                    className="w-full h-10 rounded-lg border-[#e5e7eb] hover:border-[#4682B4] focus:border-[#4682B4] flex items-center font-['DM_Sans',sans-serif]"
                  />
                ),
              },
              {
                title: <span className="font-['Lexend',sans-serif] text-[13px] text-[#475569]">Max Range</span>,
                key: 'max_range',
                width: '30%',
                render: (_: any, record: any) => (
                  <InputNumber
                    min={0}
                    step={0.01}
                    precision={2}
                    value={parseFloat(record.max_range)}
                    onChange={async (value) => {
                      if (value !== null && value !== undefined) {
                        setUpdatingLikelihood(record.id);
                        try {
                          await updateLikelihoodScore(record.id, { max_range: value });
                          getLikelihoodScores(setLikelihoodScores, setLoadingLikelihood);
                        } catch (error) {
                          notification.error({
                            message: 'Error',
                            description: 'Failed to update max range',
                          });
                        } finally {
                          setUpdatingLikelihood(null);
                        }
                      }
                    }}
                    disabled={updatingLikelihood === record.id}
                    className="w-full h-10 rounded-lg border-[#e5e7eb] hover:border-[#4682B4] focus:border-[#4682B4] flex items-center font-['DM_Sans',sans-serif]"
                  />
                ),
              },
              {
                title: <span className="font-['Lexend',sans-serif] text-[13px] text-[#475569]">Status</span>,
                key: 'status',
                width: '25%',
                align: 'center',
                render: (_: any, record: any) => (
                  updatingLikelihood === record.id ? (
                    <Tag color="processing" className="m-0 px-3 py-1 rounded-md border-none">Updating</Tag>
                  ) : (
                    <div className="bg-[#f0fdf4] border border-[#bbf7d0] rounded-md px-3 py-1 text-[#166534] text-[12px] font-medium inline-block">
                      Saved
                    </div>
                  )
                ),
              },
            ]}
          />
        </div>
      </Modal>
    </PageLayout>
  );
};

export default ScoringMethod;
